// =============================================================================
// staticSite/designBriefGenerator — brief designu z AI (prompt z DB
// `static_design_brief`, edytowalny w /prompty). Wejscie: temat mapy (macro,
// kategorie), nazwa/tagline, autor + service_label, kolory logo (map_brand_assets),
// opcjonalna wskazowka uzytkownika. Wyjscie: DesignBrief (znormalizowany —
// AI nie moze wybrac czcionki spoza puli ani koloru spoza #hex).
// Fallback bez AI: seedDesignBrief.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import { loadPrompt } from "@/services/llm/promptLoader";
import { getLLMProvider, parseLLMJSON } from "@/services/llm";
import { compilePromptSafe } from "@/services/contentEngine/promptCompiler";
import { logPublic, logError } from "@/services/contentEngine/contentLogger";
import type { StaticSiteRow } from "./types";
import { FONT_POOL, DESIGN_MOTIFS, normalizeDesignBrief, seedDesignBrief, type DesignBrief } from "./designBrief";
import { seedForDomain } from "./themes/variation";

const PREFIX = "STATIC";

const FALLBACK_PROMPT = `Jesteś dyrektorem artystycznym. Zaprojektuj system designu dla strony {{site_name}} ({{domain}}). Temat: {{macro_statement}}. Kategorie: {{categories}}. Zwróć TYLKO JSON zgodny ze schematem z instrukcji.`;

export interface DesignBriefInput {
  site: StaticSiteRow;
  /** Dodatkowa wskazowka od uzytkownika ("ciemno, jak strona bankowa", "cieplo, dla rodzicow") */
  hint?: string;
}

export interface DesignBriefResult {
  brief: DesignBrief;
  costUsd: number;
  model: string;
}

async function collectContext(site: StaticSiteRow) {
  const [mapRes, seedsRes, personaRes, brandRes, profileRes] = await Promise.all([
    (supabase as any).from("thematic_maps").select("name, macro_statement, audience, language").eq("id", site.map_id).maybeSingle(),
    (supabase as any).from("thematic_seeds").select("name, status").eq("map_id", site.map_id).limit(30),
    (supabase as any).from("map_persona").select("full_name, service_label").eq("map_id", site.map_id).maybeSingle(),
    (supabase as any).from("map_brand_assets").select("theme_color, colors").eq("map_id", site.map_id).maybeSingle(),
    (supabase as any).from("thematic_writing_profiles").select("public_author_label, voice_characteristics").eq("map_id", site.map_id).limit(1).maybeSingle(),
  ]);
  const map = mapRes?.data || {};
  const seeds = ((seedsRes?.data || []) as Array<{ name: string; status: string }>).filter((s) => s.status !== "rejected").map((s) => s.name);
  const persona = personaRes?.data || {};
  const brand = brandRes?.data || {};
  const profile = profileRes?.data || {};
  const colors: string[] = [];
  if (brand.theme_color) colors.push(String(brand.theme_color));
  if (Array.isArray(brand.colors)) for (const c of brand.colors) if (c?.hex) colors.push(String(c.hex));
  const voice = profile.voice_characteristics
    ? typeof profile.voice_characteristics === "string" ? profile.voice_characteristics : JSON.stringify(profile.voice_characteristics)
    : "";
  return {
    site_name: (map.name as string) || site.domain,
    domain: site.domain,
    tagline: site.settings?.tagline || "",
    macro_statement: (map.macro_statement as string) || "",
    audience: (map.audience as string) || "",
    language: (map.language as string) || "pl",
    categories: seeds.slice(0, 20).join(", "),
    author_name: (persona.full_name as string) || (profile.public_author_label as string) || "",
    service_label: (persona.service_label as string) || "",
    brand_colors: colors.join(", ") || "brak",
    voice_characteristics: voice.slice(0, 600),
    hint: "",
    font_pool: FONT_POOL.map((f) => `${f.id} (${f.label}; ${f.role.join("/")}; ${f.vibe})`).join("\n"),
    motifs: DESIGN_MOTIFS.join(", "),
  };
}

/** Brief deterministyczny (bez AI) — do przycisku „Losuj z domeny" i jako baza normalizacji. */
export async function seedBriefFor(site: StaticSiteRow): Promise<DesignBrief> {
  const { data: seeds } = await (supabase as any).from("thematic_seeds").select("name").eq("map_id", site.map_id).limit(1);
  return seedDesignBrief(site.theme_seed || seedForDomain(site.domain), {
    siteName: site.domain,
    tagline: site.settings?.tagline,
    firstCategory: seeds?.[0]?.name || null,
  });
}

export async function generateDesignBrief(input: DesignBriefInput): Promise<DesignBriefResult> {
  const { site } = input;
  const base = await seedBriefFor(site);
  const vars = await collectContext(site);
  vars.hint = (input.hint || "").trim() || "brak";

  const promptDb = await loadPrompt("static_design_brief" as any);
  if (!promptDb) throw new Error("Brak promptu 'static_design_brief' w bazie (Ustawienia → Prompty AI).");
  const compiled = compilePromptSafe(promptDb.template, vars, "static_design_brief", FALLBACK_PROMPT);

  const provider = getLLMProvider((promptDb.provider || "claude") as "openai" | "claude");
  const model = promptDb.modelId || promptDb.model || "claude-sonnet-5";
  logPublic(PREFIX, `Brief designu dla ${site.domain}: ${model}${input.hint ? ` (wskazówka: ${input.hint.slice(0, 60)})` : ""}`);
  try {
    const res = await provider.call(compiled, { model, step: "static_design_brief", mapId: site.map_id, temperature: promptDb.temperature ?? undefined, maxTokens: 2000 });
    const parsed = parseLLMJSON<unknown>(res.text);
    const brief = normalizeDesignBrief(parsed, base, "ai");
    logPublic(PREFIX, `Brief designu gotowy: ${brief.mode}, ${brief.fonts.heading}/${brief.fonts.body}, motyw ${brief.motif}, $${(res.costUsd ?? 0).toFixed(3)}`);
    return { brief, costUsd: res.costUsd ?? 0, model: res.modelUsed || model };
  } catch (e) {
    logError(PREFIX, "Brief designu nieudany", e);
    throw e;
  }
}
